import { useCallback, useEffect, useMemo, useState } from 'react';
import { USER_STATUS_LABELS, VERIFICATION_LABELS } from '@kulum/shared';
import {
  fetchReviewSummaries,
  type PendingReviewSummary,
  type ReviewListStatusFilter,
} from '@/lib/adminReviewService';
import { Loading } from '@/components/ui/Loading';
import { Badge, Pagination, ResizableDetailLayout } from '@/components/ui';
import { ReviewDetailPanel } from './ReviewDetailPage';

const PAGE_SIZE = 20;

const roleLabels: Record<PendingReviewSummary['role'], string> = {
  donor: 'ドナー',
  donee: 'ドニー',
  unknown: '不明',
};

const userStatusLabels: Record<PendingReviewSummary['userStatus'], string> = {
  pending: USER_STATUS_LABELS.pending,
  email_verified: USER_STATUS_LABELS.email_verified,
  under_review: USER_STATUS_LABELS.under_review,
  approved: USER_STATUS_LABELS.approved,
  rejected: USER_STATUS_LABELS.rejected,
  suspended: USER_STATUS_LABELS.suspended,
  withdrawn: USER_STATUS_LABELS.withdrawn,
  unknown: '不明',
};

const reviewStatusLabels: Record<PendingReviewSummary['reviewStatus'], string> = {
  pending: '対応待ち',
  approved: '承認済み',
  rejected: '却下',
  remanded: '差し戻し',
};

const reviewStatusVariants: Record<
  PendingReviewSummary['reviewStatus'],
  'success' | 'warning' | 'danger' | 'info'
> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  remanded: 'info',
};

const statusFilterLabels: Record<ReviewListStatusFilter, string> = {
  pending: '対応待ち',
  completed: '対応済み',
  all: 'すべて',
};

const formatDateTime = (date?: Date): string => {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export function PendingReviewsPage() {
  const [reviews, setReviews] = useState<PendingReviewSummary[]>([]);
  const [statusFilter, setStatusFilter] = useState<ReviewListStatusFilter>('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReviewUserId, setSelectedReviewUserId] = useState<string | null>(null);

  const loadReviews = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const pendingReviews = await fetchReviewSummaries(statusFilter);
      setReviews(pendingReviews);
    } catch (loadError) {
      console.error('Failed to fetch pending reviews:', loadError);
      setError('審査一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void loadReviews();
  }, [loadReviews]);

  const totalPages = Math.max(1, Math.ceil(reviews.length / PAGE_SIZE));
  const pagedReviews = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return reviews.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, reviews]);
  const visibleStart = reviews.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const visibleEnd = Math.min(currentPage * PAGE_SIZE, reviews.length);

  const handleStatusFilterChange = (nextStatusFilter: ReviewListStatusFilter) => {
    setStatusFilter(nextStatusFilter);
    setCurrentPage(1);
  };

  return (
    <ResizableDetailLayout
      isOpen={Boolean(selectedReviewUserId)}
      detail={
        selectedReviewUserId ? (
          <ReviewDetailPanel
            userId={selectedReviewUserId}
            embedded
            onClose={() => setSelectedReviewUserId(null)}
            onReviewed={() => void loadReviews()}
          />
        ) : null
      }
      list={
        <section className="space-y-5 p-5 md:p-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-admin-ink">審査管理</h2>
          <p className="mt-1 text-sm text-admin-muted">
            ドナー審査・ドニー本人確認の対応状況を確認します。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={(event) =>
              handleStatusFilterChange(event.target.value as ReviewListStatusFilter)
            }
            className="rounded-xl border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
          >
            {Object.entries(statusFilterLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void loadReviews()}
            disabled={isLoading}
            className="rounded-xl border border-admin-border bg-white px-4 py-2 text-sm font-semibold text-admin-ink transition hover:border-admin-accent hover:text-admin-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? '更新中...' : '再読み込み'}
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading ? (
        <Loading message="審査データを読み込んでいます..." />
      ) : reviews.length === 0 ? (
        <div className="rounded-2xl border border-admin-border bg-white p-8 text-center shadow-sm">
          <p className="text-admin-ink">対象の審査申請はありません。</p>
          <p className="mt-2 text-sm text-admin-muted">
            条件を変えると過去の審査申請も確認できます。
          </p>
        </div>
      ) : (
        <div className="space-y-5">
              <div className="text-xs text-admin-muted">
                表示件数: {visibleStart}-{visibleEnd} / {reviews.length}
              </div>
              <div className="overflow-hidden rounded-2xl border border-admin-border bg-white shadow-sm">
                <div className="hidden overflow-x-auto md:block">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-admin-bg text-xs uppercase tracking-wide text-admin-muted">
                      <tr>
                        <th className="px-4 py-3 font-semibold">ユーザー</th>
                        <th className="px-4 py-3 font-semibold">属性</th>
                        <th className="px-4 py-3 font-semibold">状態</th>
                        <th className="px-4 py-3 font-semibold">審査対象</th>
                        <th className="px-4 py-3 font-semibold">更新日時</th>
                        <th className="px-4 py-3 font-semibold">詳細</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-admin-border">
                      {pagedReviews.map((review) => (
                        <tr
                          key={review.userId}
                          className="cursor-pointer hover:bg-admin-bg/60"
                          onClick={() => setSelectedReviewUserId(review.userId)}
                        >
                          <td className="px-4 py-3">
                            <p className="font-semibold text-admin-ink">{review.displayName}</p>
                            <p className="text-xs text-admin-muted">{review.email}</p>
                          </td>
                          <td className="px-4 py-3 text-xs text-admin-muted">
                            <p>{roleLabels[review.role]}</p>
                            <p>{userStatusLabels[review.userStatus]}</p>
                            <p>
                              {review.residence ?? '-'}
                              {typeof review.age === 'number' ? ` / ${review.age}歳` : ''}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={reviewStatusVariants[review.reviewStatus]} size="sm">
                              {reviewStatusLabels[review.reviewStatus]}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {review.reviewTypes.map((type) => (
                                <span key={type} className={`status-pill ${review.reviewStatus}`}>
                                  {VERIFICATION_LABELS[type]}
                                </span>
                              ))}
                            </div>
                            <p className="mt-1 text-xs text-admin-muted">
                              書類 {review.documentCount} 件
                            </p>
                          </td>
                          <td className="px-4 py-3 text-xs text-admin-muted">
                            {formatDateTime(review.reviewedAt ?? review.requestedAt)}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedReviewUserId(review.userId);
                              }}
                              className="inline-flex rounded-lg bg-admin-accent px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-admin-accent-strong"
                            >
                              審査詳細
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-3 md:hidden">
                  {pagedReviews.map((review) => (
                    <article
                      key={review.userId}
                      className="cursor-pointer rounded-xl border border-admin-border bg-admin-surface p-3"
                      onClick={() => setSelectedReviewUserId(review.userId)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-admin-ink">{review.displayName}</p>
                          <p className="text-xs text-admin-muted">{review.email}</p>
                        </div>
                        <Badge variant={reviewStatusVariants[review.reviewStatus]} size="sm">
                          {reviewStatusLabels[review.reviewStatus]}
                        </Badge>
                      </div>
                      <p className="mt-2 text-xs text-admin-muted">
                        {roleLabels[review.role]} / {userStatusLabels[review.userStatus]}
                      </p>
                      <p className="text-xs text-admin-muted">
                        {review.residence ?? '-'}
                        {typeof review.age === 'number' ? ` / ${review.age}歳` : ''}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {review.reviewTypes.map((type) => (
                          <span key={type} className={`status-pill ${review.reviewStatus}`}>
                            {VERIFICATION_LABELS[type]}
                          </span>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-admin-muted">
                        更新: {formatDateTime(review.reviewedAt ?? review.requestedAt)} / 書類{' '}
                        {review.documentCount} 件
                      </p>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedReviewUserId(review.userId);
                        }}
                        className="mt-3 inline-flex rounded-lg bg-admin-accent px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-admin-accent-strong"
                      >
                        審査詳細
                      </button>
                    </article>
                  ))}
                </div>
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
        </div>
      )}
        </section>
      }
    />
  );
}
